import React, { useEffect, useState } from "react";
import { api, exportCsv, fmtMoney } from "@/lib/api";
import { Check } from "@/components/Check";
import { Plus, Download, Trash2 } from "lucide-react";
import useResizableColumns from "@/lib/useResizableColumns";
import EditableHeader from "@/components/EditableHeader";

const MEDIA_DEFAULTS = {
  0: 170, 1: 200, 2: 130, 3: 110,
  4: 90, 5: 80, 6: 100, 7: 90, 8: 110, 9: 100, 10: 100,
  11: 110, 12: 80, 13: 80,
  14: 140, 15: 100, 16: 200, 17: 48,
};

// Fallback labels — overridden by /api/settings.media_labels for each user.
const FALLBACK_LABELS = {
  page_title: "Projects",
  client: "CLIENT", property: "PROJECT", shoot_date: "DATE", shoot_time: "TIME",
  photos_40: "DELIVERABLE 1", tour_3d: "DELIVERABLE 2", floor_plans: "DELIVERABLE 3",
  package_299: "PACKAGE",
  virtual_staging: "ADD-ON 1", video_30s: "ADD-ON 2", aerial: "ADD-ON 3",
  scheduled: "SCHEDULED", payment_received: "PAID", media_done: "DONE",
  status: "STATUS", total: "TOTAL", notes: "NOTES",
};

export default function RealEstateMedia() {
  const [orders, setOrders] = useState([]);
  const [labels, setLabels] = useState(FALLBACK_LABELS);
  const { setRef, widthStyle, Grip, reset } = useResizableColumns(
    "media-v1", 18, { defaults: MEDIA_DEFAULTS },
  );

  const loadSettings = async () => {
    try {
      const s = await api.get("/settings").then((r) => r.data);
      if (s?.media_labels) setLabels({ ...FALLBACK_LABELS, ...s.media_labels });
    } catch (_) { /* noop */ }
  };

  const saveLabel = async (fieldKey, newLabel) => {
    const next = { ...labels, [fieldKey]: newLabel };
    setLabels(next);
    try {
      await api.put("/settings", { media_labels: next });
    } catch (_) { /* noop */ }
  };

  const L = (k) => labels[k] || FALLBACK_LABELS[k] || k.toUpperCase();

  const load = async () => {
    const o = await api.get("/media").then((r) => r.data);
    setOrders(Array.isArray(o) ? o : []);
  };

  useEffect(() => {
    load();
    loadSettings();
  }, []);

  const add = async () => {
    const o = await api.post("/media", { client: "" }).then((r) => r.data);
    setOrders((p) => [o, ...p]);
  };

  const update = async (id, patch) => {
    const cur = orders.find((o) => o.id === id);
    if (!cur) return;
    const next = { ...cur, ...patch };
    setOrders((p) => p.map((o) => (o.id === id ? next : o)));
    const fresh = await api.put(`/media/${id}`, next).then((r) => r.data);
    setOrders((p) => p.map((o) => (o.id === id ? fresh : o)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    setOrders((p) => p.filter((o) => o.id !== id));
    await api.delete(`/media/${id}`);
  };

  const onExport = () => exportCsv("media-orders.csv", orders);

  const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const paid = orders.filter((o) => o.payment_received).length;
  const done = orders.filter((o) => o.media_done).length;

  const statusChip = (s) => {
    const map = {
      Completed: "hud-chip-on",
      "Paid / In Progress": "hud-chip-on",
      Scheduled: "",
      "Not Started": "",
    };
    return `hud-chip ${map[s] || ""}`;
  };

  return (
    <div className="space-y-6" data-testid="real-estate-media">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">
            <EditableHeader
              label={L("page_title")}
              fieldKey="page_title"
              onSave={(v) => saveLabel("page_title", v)}
              testId="media-page-title"
            />
          </div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            {fmtMoney(revenue)}
          </h1>
          <div className="mono text-xs text-muted-foreground tracking-widest mt-2 uppercase">
            {paid} paid · {done} delivered
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={add} className="hud-btn flex items-center gap-2" data-testid="media-add">
            <Plus size={12} /> NEW ENTRY
          </button>
          <button onClick={onExport} className="hud-btn flex items-center gap-2" data-testid="media-export">
            <Download size={12} /> CSV
          </button>
          <button onClick={reset} className="hud-btn !text-[10px]" title="Reset column widths" data-testid="media-reset-widths">
            RESET WIDTHS
          </button>
        </div>
      </header>

      <div className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                <th ref={setRef(0)} style={widthStyle(0, "170px")}>
                  <EditableHeader label={L("client")} fieldKey="client" onSave={(v) => saveLabel("client", v)} testId="hdr-client" />
                  <Grip index={0} />
                </th>
                <th ref={setRef(1)} style={widthStyle(1, "200px")}>
                  <EditableHeader label={L("property")} fieldKey="property" onSave={(v) => saveLabel("property", v)} testId="hdr-property" />
                  <Grip index={1} />
                </th>
                <th ref={setRef(2)} style={widthStyle(2, "130px")}>
                  <EditableHeader label={L("shoot_date")} fieldKey="shoot_date" onSave={(v) => saveLabel("shoot_date", v)} testId="hdr-shoot-date" />
                  <Grip index={2} />
                </th>
                <th ref={setRef(3)} style={widthStyle(3, "110px")}>
                  <EditableHeader label={L("shoot_time")} fieldKey="shoot_time" onSave={(v) => saveLabel("shoot_time", v)} />
                  <Grip index={3} />
                </th>
                <th ref={setRef(4)} style={widthStyle(4, "90px")} className="!text-center">
                  <EditableHeader label={L("photos_40")} fieldKey="photos_40" onSave={(v) => saveLabel("photos_40", v)} />
                  <Grip index={4} />
                </th>
                <th ref={setRef(5)} style={widthStyle(5, "80px")} className="!text-center">
                  <EditableHeader label={L("tour_3d")} fieldKey="tour_3d" onSave={(v) => saveLabel("tour_3d", v)} />
                  <Grip index={5} />
                </th>
                <th ref={setRef(6)} style={widthStyle(6, "100px")} className="!text-center">
                  <EditableHeader label={L("floor_plans")} fieldKey="floor_plans" onSave={(v) => saveLabel("floor_plans", v)} />
                  <Grip index={6} />
                </th>
                <th ref={setRef(7)} style={widthStyle(7, "90px")} className="!text-center">
                  <EditableHeader label={L("package_299")} fieldKey="package_299" onSave={(v) => saveLabel("package_299", v)} />
                  <Grip index={7} />
                </th>
                <th ref={setRef(8)} style={widthStyle(8, "110px")} className="!text-center">
                  <EditableHeader label={L("virtual_staging")} fieldKey="virtual_staging" onSave={(v) => saveLabel("virtual_staging", v)} />
                  <Grip index={8} />
                </th>
                <th ref={setRef(9)} style={widthStyle(9, "100px")} className="!text-center">
                  <EditableHeader label={L("video_30s")} fieldKey="video_30s" onSave={(v) => saveLabel("video_30s", v)} />
                  <Grip index={9} />
                </th>
                <th ref={setRef(10)} style={widthStyle(10, "100px")} className="!text-center">
                  <EditableHeader label={L("aerial")} fieldKey="aerial" onSave={(v) => saveLabel("aerial", v)} />
                  <Grip index={10} />
                </th>
                <th ref={setRef(11)} style={widthStyle(11, "110px")} className="!text-center">
                  <EditableHeader label={L("scheduled")} fieldKey="scheduled" onSave={(v) => saveLabel("scheduled", v)} />
                  <Grip index={11} />
                </th>
                <th ref={setRef(12)} style={widthStyle(12, "80px")} className="!text-center">
                  <EditableHeader label={L("payment_received")} fieldKey="payment_received" onSave={(v) => saveLabel("payment_received", v)} />
                  <Grip index={12} />
                </th>
                <th ref={setRef(13)} style={widthStyle(13, "80px")} className="!text-center">
                  <EditableHeader label={L("media_done")} fieldKey="media_done" onSave={(v) => saveLabel("media_done", v)} />
                  <Grip index={13} />
                </th>
                <th ref={setRef(14)} style={widthStyle(14, "140px")}>
                  <EditableHeader label={L("status")} fieldKey="status" onSave={(v) => saveLabel("status", v)} />
                  <Grip index={14} />
                </th>
                <th ref={setRef(15)} style={widthStyle(15, "100px")} className="!text-right">
                  <EditableHeader label={L("total")} fieldKey="total" onSave={(v) => saveLabel("total", v)} />
                  <Grip index={15} />
                </th>
                <th ref={setRef(16)} style={widthStyle(16, "200px")}>
                  <EditableHeader label={L("notes")} fieldKey="notes" onSave={(v) => saveLabel("notes", v)} />
                  <Grip index={16} />
                </th>
                <th ref={setRef(17)} style={widthStyle(17, "48px")} /></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`media-row-${o.id}`}>
                  <td>
                    <input
                      defaultValue={o.client}
                      onBlur={(e) =>
                        e.target.value !== o.client && update(o.id, { client: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Client"
                    />
                  </td>
                  <td>
                    <input
                      defaultValue={o.property}
                      onBlur={(e) =>
                        e.target.value !== o.property &&
                        update(o.id, { property: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Property"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      defaultValue={o.shoot_date || ""}
                      onBlur={(e) => update(o.id, { shoot_date: e.target.value || null })}
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      defaultValue={o.shoot_time || ""}
                      onBlur={(e) => update(o.id, { shoot_time: e.target.value || null })}
                      className="hud-input !py-1"
                    />
                  </td>
                  {[
                    "photos_40", "tour_3d", "floor_plans",
                  ].map((k) => (
                    <td key={k} className="!text-center">
                      <Check
                        checked={!!o[k]}
                        onChange={(v) => update(o.id, { [k]: v })}
                        testid={`media-${k}-${o.id}`}
                      />
                    </td>
                  ))}
                  <td className="!text-center">
                    <span className={`hud-chip ${o.package_299 ? "hud-chip-on" : ""}`}>
                      {o.package_299 ? "$299" : "—"}
                    </span>
                  </td>
                  {[
                    "virtual_staging", "video_30s", "aerial",
                    "scheduled", "payment_received", "media_done",
                  ].map((k) => (
                    <td key={k} className="!text-center">
                      <Check
                        checked={!!o[k]}
                        onChange={(v) => update(o.id, { [k]: v })}
                        testid={`media-${k}-${o.id}`}
                      />
                    </td>
                  ))}
                  <td>
                    <span className={statusChip(o.status)}>{o.status}</span>
                  </td>
                  <td className="num font-semibold text-[hsl(var(--oxblood))]">
                    {fmtMoney(o.total)}
                  </td>
                  <td>
                    <input
                      defaultValue={o.notes}
                      onBlur={(e) =>
                        e.target.value !== o.notes && update(o.id, { notes: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Notes"
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => remove(o.id)}
                      className="text-muted-foreground hover:text-[hsl(var(--signal))]"
                      data-testid={`media-delete-${o.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={18} className="text-center text-muted-foreground py-10">
                    No media orders yet. Click <span className="text-[hsl(var(--oxblood))]">NEW ORDER</span> to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mono text-[11px] text-muted-foreground uppercase tracking-widest">
        {labels.page_title === "Real Estate Media"
          ? "$299 PACKAGE = 40 PHOTOS + 3D TOUR + FLOORPLAN · STAGING $150 · VIDEO $100 · AERIAL $100"
          : `${L("package_299")} = ${L("photos_40")} + ${L("tour_3d")} + ${L("floor_plans")} · ${L("virtual_staging")} · ${L("video_30s")} · ${L("aerial")}`}
      </div>
    </div>
  );
}
